import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { get, post as apiPost, del } from '../utils/api';
import { BUSINESS_CATEGORIES, getCategoryName, getCuisineName } from '../utils/constants';
import {
  Search, MapPin, Heart, Star, Filter, Utensils, Coffee, Wine,
  Beer, Truck, Store, ShoppingBasket, Croissant, IceCream2,
  Loader2, X, Building2, Eye, ShoppingBag, Bell, BookOpen, ShieldCheck, Map,
  Flame, Gamepad2, Music, Camera, TreePine
} from 'lucide-react';
import AdBanner from '../components/AdBanner';

const categoryIcons = {
  restaurante: Utensils, restobar: Wine, panaderia: Croissant,
  cafeteria: Coffee, carretas: Truck, agachadito: Flame,
  tienda_gourmet: Store, bar: Beer, tienda_de_barrio: ShoppingBasket,
  heladeria: IceCream2, sala_de_juegos: Gamepad2, club: Music,
  foto_estudio: Camera, parque: TreePine, bazar: ShoppingBag,
  tecnologia: Building2,
};

const Home = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [priceFilter, setPriceFilter] = useState(null);
  const [cityFilter, setCityFilter] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [availableCities, setAvailableCities] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [visitStatus, setVisitStatus] = useState(null);

  const isPremium = user && ['premium_plus', 'premium_annual', 'premium_basic', 'premium'].includes(user.subscription_plan);
  const isExemptUser = isPremium || user?.role === 'owner' || user?.role === 'admin';

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(
      (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, timeout: 5000 }
    );
  }, []);

  useEffect(() => {
    if (user && !isExemptUser) fetchVisitStatus();
  }, [user]);

  const fetchVisitStatus = async () => {
    try {
      const data = await get('/api/users/visit-status');
      setVisitStatus(data);
    } catch {}
  };

  useEffect(() => { fetchRestaurants(); }, [selectedCategory]);

  const fetchRestaurants = async () => {
    setLoading(true);
    try {
      let url = '/api/restaurants?limit=50';
      if (selectedCategory) url += `&category=${selectedCategory}`;
      const data = await get(url);
      const list = data.restaurants || [];
      setRestaurants(list);
      const cities = new Set();
      list.forEach(r => r.branches?.forEach(b => { if (b.city) cities.add(b.city); }));
      setAvailableCities([...cities].sort());
    } catch {} finally { setLoading(false); }
  };

  const toggleFavorite = async (restaurantId, isFavorite) => {
    try {
      if (isFavorite) await del(`/api/users/favorites/${restaurantId}`);
      else await apiPost(`/api/users/favorites/${restaurantId}`, {});
      refreshUser();
    } catch {}
  };

  const calcDistance = useCallback((branches) => {
    if (!userLocation || !branches?.length) return null;
    const { lat, lng } = userLocation;
    let minDist = Infinity;
    for (const b of branches) {
      const bLat = b.latitude || b.location?.coordinates?.[1];
      const bLng = b.longitude || b.location?.coordinates?.[0];
      if (!bLat || !bLng) continue;
      const R = 6371;
      const dLat = (bLat - lat) * Math.PI / 180;
      const dLon = (bLng - lng) * Math.PI / 180;
      const a = Math.sin(dLat/2)**2 + Math.cos(lat*Math.PI/180) * Math.cos(bLat*Math.PI/180) * Math.sin(dLon/2)**2;
      const d = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      if (d < minDist) minDist = d;
    }
    return minDist === Infinity ? null : minDist;
  }, [userLocation]);

  const filteredRestaurants = restaurants
    .filter(r => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!(r.name?.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q) ||
              r.cuisine_type?.toLowerCase().includes(q) || r.branches?.some(b =>
                b.name?.toLowerCase().includes(q) || b.address?.toLowerCase().includes(q) || b.city?.toLowerCase().includes(q)
              ))) return false;
      }
      if (priceFilter && r.price_range !== priceFilter) return false;
      if (cityFilter && !r.branches?.some(b => b.city === cityFilter)) return false;
      return true;
    })
    .sort((a, b) => {
      // Aliados always appear first
      const aAliado = a.is_aliado ? 1 : 0;
      const bAliado = b.is_aliado ? 1 : 0;
      if (bAliado !== aAliado) return bAliado - aAliado;
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'favorites') return (b.total_favorites || 0) - (a.total_favorites || 0);
      if (sortBy === 'price_low') return (a.price_range || 2) - (b.price_range || 2);
      if (sortBy === 'price_high') return (b.price_range || 2) - (a.price_range || 2);
      return 0;
    });

  const activeFilterCount = [selectedCategory, priceFilter, cityFilter].filter(Boolean).length;

  const isRestaurantFavorite = (restaurant) => {
    const favIds = (user?.favorites || []).map(f => typeof f === 'string' ? f : String(f));
    return favIds.includes(restaurant._id);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-emerald-600 pt-12 pb-6 px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-teal-100 text-sm">Hola, {user?.name?.split(' ')[0] || 'Usuario'}</p>
            <h1 className="text-2xl font-bold text-white">Descubre lugares</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/map')} className="w-9 h-9 bg-white/20 backdrop-blur rounded-full flex items-center justify-center hover:bg-white/30 transition-colors" data-testid="home-map-btn">
              <Map className="w-4 h-4 text-white" />
            </button>
            <button onClick={() => navigate('/blog')} className="w-9 h-9 bg-white/20 backdrop-blur rounded-full flex items-center justify-center hover:bg-white/30 transition-colors" data-testid="home-blog-btn">
              <BookOpen className="w-4 h-4 text-white" />
            </button>
            <button onClick={() => navigate('/notifications')} className="w-9 h-9 bg-white/20 backdrop-blur rounded-full flex items-center justify-center hover:bg-white/30 transition-colors" data-testid="home-notifications-btn">
              <Bell className="w-4 h-4 text-white" />
            </button>
            <div className="bg-white/20 backdrop-blur px-3 py-1.5 rounded-full flex items-center gap-1">
              <Star className="w-4 h-4 text-yellow-300 fill-yellow-300" />
              <span className="text-white font-semibold text-sm">{user?.points || 0}</span>
            </div>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
          <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar restaurantes, cafeterias..." data-testid="search-input"
            className="w-full pl-12 pr-12 py-3.5 bg-white dark:bg-gray-800 rounded-xl shadow-lg focus:ring-2 focus:ring-teal-300 outline-none" />
          <button onClick={() => setShowFilters(!showFilters)}
            className={`absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-colors ${showFilters || activeFilterCount > 0 ? 'bg-teal-100 text-teal-600' : 'text-gray-400 dark:text-gray-500 hover:bg-gray-100'}`}
            data-testid="filter-toggle-btn">
            <Filter className="w-5 h-5" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">{activeFilterCount}</span>
            )}
          </button>
        </div>

        {/* Visit Counters for Free Users */}
        {!isExemptUser && visitStatus && !visitStatus.unlimited && (
          <div className="flex items-center gap-2 mt-3" data-testid="visit-counters">
            <div className="flex-1 bg-white/15 backdrop-blur rounded-xl px-3 py-2 flex items-center gap-2">
              <Eye className="w-4 h-4 text-teal-100" />
              <div className="flex-1">
                <p className="text-[10px] text-teal-100 leading-tight">Visitas diarias</p>
                <p className="text-sm font-bold text-white">{visitStatus.remaining} / 3</p>
              </div>
            </div>
            <div className="flex-1 bg-white/15 backdrop-blur rounded-xl px-3 py-2 flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-amber-200" />
              <div className="flex-1">
                <p className="text-[10px] text-teal-100 leading-tight">Compradas</p>
                <p className="text-sm font-bold text-white">{visitStatus.purchased_searches || 0}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="px-6 py-4 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700/40 shadow-sm dark:shadow-black/20 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-700 dark:text-gray-200 text-sm">Categoria</h3>
              {selectedCategory && <button onClick={() => setSelectedCategory(null)} className="text-xs text-teal-600 flex items-center gap-1"><X className="w-3 h-3" /> Limpiar</button>}
            </div>
            <div className="flex flex-wrap gap-2">
              {BUSINESS_CATEGORIES.map((cat) => {
                const Icon = categoryIcons[cat.id] || Utensils;
                return (
                  <button key={cat.id} onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedCategory === cat.id ? 'bg-teal-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200'}`}
                    data-testid={`category-${cat.id}`}>
                    <Icon className="w-3.5 h-3.5" /> {cat.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <h3 className="font-semibold text-gray-700 dark:text-gray-200 text-sm mb-2">Precio</h3>
            <div className="flex gap-2">
              {[{v:1,l:'$',d:'Economico'},{v:2,l:'$$',d:'Moderado'},{v:3,l:'$$$',d:'Premium'}].map(p => (
                <button key={p.v} onClick={() => setPriceFilter(priceFilter === p.v ? null : p.v)}
                  className={`flex-1 py-2 px-3 rounded-xl text-center transition-colors ${priceFilter === p.v ? 'bg-teal-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200'}`}
                  data-testid={`price-filter-${p.v}`}>
                  <span className="font-bold text-sm">{p.l}</span><p className="text-[10px] mt-0.5">{p.d}</p>
                </button>
              ))}
            </div>
          </div>
          {availableCities.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-700 dark:text-gray-200 text-sm mb-2">Ciudad</h3>
              <div className="flex flex-wrap gap-2">
                {availableCities.map(city => (
                  <button key={city} onClick={() => setCityFilter(cityFilter === city ? '' : city)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${cityFilter === city ? 'bg-teal-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200'}`}
                    data-testid={`city-filter-${city}`}>
                    <MapPin className="w-3 h-3 inline mr-1" />{city}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <h3 className="font-semibold text-gray-700 dark:text-gray-200 text-sm mb-2">Ordenar por</h3>
            <div className="flex flex-wrap gap-2">
              {[{v:'recent',l:'Recientes'},{v:'name',l:'Nombre'},{v:'favorites',l:'Favoritos'},{v:'price_low',l:'Menor precio'},{v:'price_high',l:'Mayor precio'}].map(s => (
                <button key={s.v} onClick={() => setSortBy(s.v)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${sortBy === s.v ? 'bg-indigo-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200'}`}
                  data-testid={`sort-${s.v}`}>{s.l}</button>
              ))}
            </div>
          </div>
          {activeFilterCount > 0 && (
            <button onClick={() => { setSelectedCategory(null); setPriceFilter(null); setCityFilter(''); setSortBy('recent'); }}
              className="w-full py-2 text-sm text-red-500 font-medium hover:bg-red-50 rounded-lg" data-testid="clear-all-filters-btn">
              Limpiar todos los filtros
            </button>
          )}
        </div>
      )}

      {/* AdBanner removed - restore after AdSense approval: <AdBanner size="largeBanner" className="py-3" /> */}

      {/* Quick Categories */}
      <div className="px-6 py-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">Categorias</h2>
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
          {BUSINESS_CATEGORIES.slice(0, 6).map((cat) => {
            const Icon = categoryIcons[cat.id] || Utensils;
            return (
              <button key={cat.id} onClick={() => { setSelectedCategory(selectedCategory === cat.id ? null : cat.id); setShowFilters(true); }}
                className={`flex flex-col items-center gap-2 min-w-[80px] p-3 rounded-xl transition-all ${selectedCategory === cat.id ? 'bg-teal-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 shadow-sm hover:shadow-md'}`}>
                <Icon className="w-6 h-6" />
                <span className="text-xs font-medium text-center">{cat.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Restaurants Grid */}
      <div className="px-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            {selectedCategory ? getCategoryName(selectedCategory) : 'Todos los lugares'}
          </h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">{filteredRestaurants.length} resultados</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
          </div>
        ) : filteredRestaurants.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="text-gray-500 dark:text-gray-400">No se encontraron resultados</p>
            <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Intenta con otra busqueda</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filteredRestaurants.map((restaurant) => (
              <RestaurantGridCard
                key={restaurant._id}
                restaurant={restaurant}
                onClick={() => navigate(`/restaurant/${restaurant._id}`)}
                isFavorite={isRestaurantFavorite(restaurant)}
                onToggleFavorite={(e) => {
                  e.stopPropagation();
                  toggleFavorite(restaurant._id, isRestaurantFavorite(restaurant));
                }}
                distance={calcDistance(restaurant.branches)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ======================== GRID CARD ========================
const RestaurantGridCard = ({ restaurant, onClick, isFavorite, onToggleFavorite, distance }) => {
  const Icon = categoryIcons[restaurant.category] || Utensils;
  const image = restaurant.logo_base64 || restaurant.cover_image_base64 || (restaurant.images_base64?.[0]);
  const mainBranch = restaurant.branches?.[0];
  const branchCount = restaurant.branches?.length || 0;
  const totalFavs = restaurant.total_favorites || 0;

  return (
    <div onClick={onClick} className="bg-white dark:bg-gray-800 rounded-[20px] overflow-hidden cursor-pointer shadow-[0_4px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_6px_24px_rgba(0,0,0,0.12)] transition-shadow"
      data-testid={`restaurant-card-${restaurant._id}`}>
      {/* Image container - square */}
      <div className="relative w-full" style={{ aspectRatio: '1/1' }}>
        {image ? (
          <img src={image} alt={restaurant.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            <Icon className="w-12 h-12 text-gray-300" />
          </div>
        )}

        {/* Distance badge - top left */}
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-xl" data-testid={`distance-badge-${restaurant._id}`}>
          <MapPin className="w-3 h-3 text-white" />
          <span className="text-white text-[11px] font-semibold">
            {distance != null ? (distance < 1 ? `${(distance * 1000).toFixed(0)} m` : `${distance.toFixed(1)} km`) : (mainBranch?.city || 'EC')}
          </span>
        </div>

        {/* Favorite button - top right */}
        <button onClick={onToggleFavorite}
          className="absolute top-2.5 right-2.5 w-[34px] h-[34px] rounded-full bg-black/35 flex items-center justify-center hover:bg-black/50 transition-colors"
          data-testid={`fav-btn-${restaurant._id}`}>
          <Heart className={`w-[18px] h-[18px] ${isFavorite ? 'text-[#FF385C] fill-[#FF385C]' : 'text-white'}`} />
        </button>
      </div>

      {/* Content */}
      <div className="py-2.5 px-2.5 text-center">
        <h3 className="text-[15px] font-bold text-gray-800 dark:text-gray-100 leading-tight line-clamp-1 tracking-tight" data-testid={`card-name-${restaurant._id}`}>
          {restaurant.name}
        </h3>
        {restaurant.is_aliado && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full mt-0.5">
            <ShieldCheck className="w-3 h-3" /> Aliado
          </span>
        )}
        <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
          {restaurant.cuisine_type ? getCuisineName(restaurant.cuisine_type) : getCategoryName(restaurant.category)}
        </p>
        <div className="flex items-center justify-center gap-3 mt-1.5">
          <span className="flex items-center gap-1 text-[12px] text-gray-500 dark:text-gray-400 font-medium">
            <Building2 className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" /> {branchCount} {branchCount === 1 ? 'sucursal' : 'sucursales'}
          </span>
          {totalFavs > 0 && (
            <span className="flex items-center gap-1 text-[12px] text-gray-500 dark:text-gray-400 font-medium">
              <Heart className="w-3.5 h-3.5 text-[#FF385C] fill-[#FF385C]" /> {totalFavs}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
